import axios, { AxiosInstance } from 'axios';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Local, Session } from '@/utils/storage';
import log from '@/utils/log';
import qs from 'qs';
import { get, debounce } from 'lodash';
import storesAppConfig from '@/store/appConfig';
import { reloadChannel } from '@/utils/channel';

/* ----------------------- S 此处处理移动端和PC端不同的过期提示。移动端出后可重构 ---------------------- */
/**
 * 登录过期提示，主要用于兼容移动端和PC端不同的登录提示
 * @param error
 * @returns
 */
const expiredFunc = (error: any, message?: string, navCallback?: () => void) => {
	//特殊处理
	if (!errorController.handled) return;
	error.message = message || '未授权，请登录';
	Session.clear(); // 清除浏览器全部临时缓存
	Local.clear();
	errorController.handled = false;
	ElMessageBox.confirm(message || '您已被登出，请重新登录', '提示', {
		showCancelButton: false,
		confirmButtonClass: 'el-button el-button--primary el-button--large waves-effect',
		// cancelButtonClass: "el-button el-button--large is-plain"
	})
		.then(() => {
			errorController.handled = true;
			navCallback && navCallback();
		})
		.catch(() => {});
};

const RouterExpiredMapObj: Record<string, any> = {
	share: ['/ETaxLogin'],
	report: ['/mobile/jsbg', '/mobile/qysjbglb', '/mobile/qybgfx'],
};

const handleExpired = (() => {
	// 获取hash并去除#号
	const hash = window.location.hash;
	return (error: any) => {
		const shareRoutes = RouterExpiredMapObj['share'];
		const reportRoutes = RouterExpiredMapObj['report'];
		// 处理分享过期
		for (let i = 0; i < shareRoutes.length; i++) {
			if (hash.indexOf(shareRoutes[i]) > -1) {
				expiredFunc(error, '分享链接已过期');
				return;
			}
		}
		// 处理极速报告登录过期代码
		for (let i = 0; i < reportRoutes.length; i++) {
			if (hash.indexOf(reportRoutes[i]) > -1) {
				expiredFunc(error, '登录已过期，请重新登录', () => {
					navToJsbgHome();
				});
				Session.clear();
				Session.remove('openId');
				return;
			}
		}
		// 处理PC端登录过期
		expiredFunc(error, '', () => {
			reloadChannel('logout');
		});
	};
})();

/**
 * 此方法为处理极速报告过期后重新登录
 */
const navToJsbgHome = async () => {
	const hash = window.location.hash;
	if (hash == '#/mobile/jsbg') return window.location.reload();
	var baseUrl = window.location.origin + window.location.pathname;
	const redirectUri = `${baseUrl}#/mobile/jsbg`;
	window.location.href = redirectUri;
};

/* ----------------------- E 以上处理移动端和PC端不同的过期提示。移动端出后可重构 ---------------------- */

// 配置新建一个 axios 实例
const service: AxiosInstance | any = axios.create({
	baseURL: import.meta.env.VITE_API_PREFIX,
	timeout: import.meta!.env.DEV ? 30000 : 60000,
	headers: { 'Content-Type': 'application/json' },
	paramsSerializer: {
		serialize(params) {
			return qs.stringify(params, { allowDots: true });
		},
	},
});

// 添加请求拦截器
service.interceptors.request.use(
	(config: { headers: { [x: string]: string } }) => {
		// 在发送请求之前做些什么 token
		if (Session.get('token')) config.headers!['Authorization'] = `${Session.get('token')}`;
		if (storesAppConfig.code) config.headers['App-Id'] = storesAppConfig.code;
		// if (storesAppConfig.code && `${Session.get("loginAppId")}` != 'null') {
		//   console.log(`${Session.get("loginAppId")}`,111);

		//   config.headers["App-Id"] = `${Session.get("loginAppId")}`
		// } else
		//  config.headers["App-Id"] = ''
		// config.headers["App-Id"] = `${Session.get("loginAppId")}` == null ? '' : `${Session.get("loginAppId")}`
		// 注：此处Ent-id应后端要求取值为tyshxydm
		if (Session.get('tyshxydm')) config.headers['Ent-Id'] = `${Session.get('tyshxydm')}`;
		return config;
	},
	(error: any) => {
		// 对请求错误做些什么
		return Promise.reject(error);
	}
);

// 添加响应拦截器
service.interceptors.response.use(
	(response: any) => {
		const { config } = response;
		// 对下载文件做特殊处理 - 如果请求指定了responseType为blob，直接返回响应
		if (config.responseType === 'blob') {
			return response;
		}
		// 对非JSON响应做特殊处理
		if (!response.headers['content-type']?.includes('application/json')) {
			return response;
		}
		// 对响应数据做点什么
		const res = response.data;
		const { code, msg } = res;
		// 状态码不存在则非后端请求接口
		if (code === undefined) {
			return Promise.reject(response);
		}
		// 处理后端状态码
		switch (code) {
			case 200:
				return res;
			default:
				const error = { message: msg };
				!config.silent && getErrorMsg(error, code);
		}
		return Promise.reject(response);
	},
	(error: any) => {
		if (!error.response) {
			// 来自服务端或浏览器
			if (error.message.indexOf('timeout') != -1) {
				error.message = '请求超时';
			} else if (error.message == 'Network Error') {
				error.message = '网络连接错误';
			} else {
				getErrorMsg(error, get(error, 'request.status'));
			}
		} else {
			// 来自API接口
			let { status, code, msg } = get(error, 'response.data');
			status = status || code;
			status = status || get(error, 'response.status');
			error.message = msg || get(error, 'response.data.error_description');
			error.message = error.message || get(error, 'response.statusText');
			getErrorMsg(error, status);
		}

		errorLog(error);
		return Promise.reject(error);
	}
);

function getErrorMsg(error: any, status: number) {
	switch (status) {
		case 10001:
			error.message = '登录失败：' + error.message;
			break;
		case 400:
			error.message = error.message || '请求错误，请稍后再试！';
			break;
		case 401:
			handleExpired(error);
			break;
		case 403:
			// error.message = '拒绝访问';
			break;
		case 404:
			error.message = '请求地址不存在';
			break;
		case 408:
			error.message = '请求超时';
			break;
		case 500:
			// error.message = error.message;
			break;
		case 501:
			error.message = '服务未实现';
			break;
		case 502:
			error.message = '网关错误';
			break;
		case 503:
			error.message = '服务不可用';
			break;
		case 504:
			error.message = '网关超时';
			break;
		case 505:
			error.message = 'HTTP版本不受支持';
			break;
		default:
			break;
	}
	errorLog(error);
}

// 异常控制器，主要用于限流
export const errorController = {
	error: undefined as any,
	handled: true, //如果是false，则表示有其它逻辑正在处理，不希望被打扰
	handler: debounce(showErrorMsg, 500), //限流
};

// 记录和显示错误
export function errorLog(error: any) {
	// 打印到控制台
	if (import.meta.env.DEV) {
		log.danger('>>>>>> Error >>>>>>');
		// eslint-disable-next-line no-console
		console.log(error);
	}

	//执行控制器显示错误信息
	errorController.error = error;
	errorController.handler();
}

function showErrorMsg() {
	if (!errorController.handled) return;
	if (errorController.error.message == '登录配置信息不存在') return;
	// 显示提示
	ElMessage({
		message: errorController.error.message,
		type: 'error',
		duration: 2 * 1000,
	});
}

// 导出 axios 实例
export default service;
